import { opportunityDocumentsService } from '@/services/opportunity-documents.service';

const POLL_INTERVAL_MS = 4000;

const initialState = () => ({
  opportunityId: null,
  documentsByQueue: {},
  pollTimerId: null,
  watchers: [],
  subscribers: [],
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  getOpportunityQueuedDocuments({ commit }, { opportunityId, cancelToken }) {
    commit('GET_QUEUED_DOCUMENTS');
    return opportunityDocumentsService
      .getDocumentQueue({ opportunityId, cancelToken })
      .then((documentsByQueue) => {
        commit('GET_QUEUED_DOCUMENTS_SUCCESS', { opportunityId, documentsByQueue });
        return documentsByQueue;
      })
      .catch((err) => commit('ERROR', err));
  },

  pollForQueuedDocuments({ dispatch, commit, state }, { opportunityId, signal = null, onKeywordIndexerDone = null }) {
    if (signal && signal.aborted) return;

    if (state.opportunityId !== null && state.opportunityId !== opportunityId) {
      commit('RESET_POLLER');
    }

    const isNewLoop = state.opportunityId === null;
    const subscriber = { signal, onKeywordIndexerDone, abortHandler: null };

    if (signal) {
      subscriber.abortHandler = () => {
        commit('REMOVE_SUBSCRIBER', { subscriber });
        if (state.subscribers.length === 0) {
          commit('RESET_POLLER');
        }
      };
      signal.addEventListener('abort', subscriber.abortHandler, { once: true });
    }

    commit('ADD_SUBSCRIBER', { opportunityId, subscriber });

    if (!isNewLoop) return subscriber;

    dispatch('getOpportunityQueuedDocuments', { opportunityId }).then(() => {
      if (state.opportunityId !== opportunityId) return;

      const watchers = [];
      const keywordIndexerQueueHasMessages =
        state.documentsByQueue.keywordIndexer && state.documentsByQueue.keywordIndexer.length > 0;
      if (keywordIndexerQueueHasMessages) {
        watchers.push({
          queueName: 'keywordIndexer',
          action: 'opportunityDocumentsPdfEngine/getOpportunityMatchInformation',
          args: { opportunityId },
        });
      }

      if (watchers.length === 0) {
        const subscribersToNotify = state.subscribers.slice();
        commit('RESET_POLLER');
        subscribersToNotify.forEach((s) => s.onKeywordIndexerDone && s.onKeywordIndexerDone());
        return;
      }

      const timerId = setInterval(() => {
        dispatch('getOpportunityQueuedDocuments', { opportunityId }).then(async () => {
          if (state.opportunityId !== opportunityId) return;
          for (let i = state.watchers.length - 1; i >= 0; i--) {
            const { queueName, action, args } = state.watchers[i];
            if (state.documentsByQueue[queueName] && state.documentsByQueue[queueName].length === 0) {
              if (action) await dispatch(action, args, { root: true });
              if (state.opportunityId !== opportunityId) return;
              if (queueName === 'keywordIndexer') {
                state.subscribers.forEach((s) => s.onKeywordIndexerDone && s.onKeywordIndexerDone());
              }
              commit('REMOVE_WATCHER', { index: i });
            }
          }
          if (state.opportunityId === opportunityId && state.watchers.length === 0) {
            commit('RESET_POLLER');
          }
        });
      }, POLL_INTERVAL_MS);

      commit('SET_POLL_TIMER', { timerId, watchers });
    });

    return subscriber;
  },

  stopPollForQueuedDocuments({ commit, state }, { subscriber } = {}) {
    if (!subscriber) return;
    if (subscriber.signal && subscriber.abortHandler) {
      subscriber.signal.removeEventListener('abort', subscriber.abortHandler);
    }
    commit('REMOVE_SUBSCRIBER', { subscriber });
    if (state.subscribers.length === 0) {
      commit('RESET_POLLER');
    }
  },
};

const mutations = {
  GET_QUEUED_DOCUMENTS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_QUEUED_DOCUMENTS_SUCCESS(state, { documentsByQueue }) {
    state.status = {
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
    state.documentsByQueue = documentsByQueue || {};
  },
  ADD_SUBSCRIBER(state, { opportunityId, subscriber }) {
    if (state.opportunityId === null) state.opportunityId = opportunityId;
    state.subscribers.push(subscriber);
  },
  REMOVE_SUBSCRIBER(state, { subscriber }) {
    const idx = state.subscribers.indexOf(subscriber);
    if (idx >= 0) state.subscribers.splice(idx, 1);
  },
  SET_POLL_TIMER(state, { timerId, watchers }) {
    state.pollTimerId = timerId;
    state.watchers = watchers;
  },
  REMOVE_WATCHER(state, { index }) {
    state.watchers.splice(index, 1);
  },
  RESET_POLLER(state) {
    if (state.pollTimerId !== null) {
      clearInterval(state.pollTimerId);
      state.pollTimerId = null;
    }
    state.subscribers.forEach((s) => {
      if (s.signal && s.abortHandler) {
        s.signal.removeEventListener('abort', s.abortHandler);
      }
    });
    state.subscribers = [];
    state.watchers = [];
    state.opportunityId = null;
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
};

const getters = {
  hasScheduledKeywordIndexing: (state) =>
    Array.isArray(state.documentsByQueue['keywordIndexer']) &&
    state.documentsByQueue['keywordIndexer'].some((msg) => msg.body.messageType === 'documents_pipeline_run'),
  queueMessages: (state) => {
    const messages = state.documentsByQueue['keywordIndexer'];
    return Array.isArray(messages) && messages.length > 0 ? messages : [];
  },
  // TODO: remove
  keywordQueuePosition: (state) => {
    const docs = state.documentsByQueue['keywordIndexer'];
    if (!Array.isArray(docs) || docs.length === 0) return null;
    const first = docs[0];
    if (!first || typeof first.queueOrder !== 'number') return null;
    return { position: first.queueOrder + 1 };
  },

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const opportunityDocumentsQueue = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
